/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, getter-return, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import {
  findBy,
  mapBy,
  sortBy,
  uniq,
} from '@linc-technologies/linced-common/utils/arrays';
import { isArray } from '@ember/array';
import { TrackedArray } from 'tracked-built-ins';
import { get, set, action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty, isPresent } from '@ember/utils';
import Area from 'linced-app/enums/nz-moe-area';
import DepartureReason from 'linced-app/enums/nz-moe-departure-reason';
import EnrolmentStatus from 'linced-app/enums/enrolment-status';
import PostSchoolActivity from 'linced-app/enums/nz-moe-post-school-activity';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import NzMoeSchool from '@linc-technologies/linced-common/enums/nz-moe-school';
import Options from '@linc-technologies/linced-common/enums/options';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import SchoolStatus from '@linc-technologies/linced-common/enums/school-status';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import moment from 'moment';
import { alias } from '@ember/object/computed';

const NEEDS_FIELD = {
  destinationSchool: ['S', 'K', 'C', 'P'],
  destinationArea: ['S', 'K', 'C', 'P'],
  postSchoolActivity: ['O', 'X', 'L', 'E', 'J'],
  flags: ['S', 'H', 'K', 'C', 'P'],
};

export default class UnenrolController extends Controller {
  @action
  setDefaultDate(value) {
    this.defaultDate = value;
  }

  @action
  setYearLevel(value) {
    this.yearLevel = value;
  }

  // Services
  @service
  dates;

  @service
  fieldOptions;

  @service
  options;

  @service
  permissions;

  @service
  router;

  @service
  session;

  @service
  store;

  @service
  strings;

  DepartureReason = DepartureReason; // TODO: Handle other jurisdictions
  Jurisdiction = Jurisdiction;
  NzMoeSchool = NzMoeSchool;
  Area = Area;
  PostSchoolActivity = PostSchoolActivity;

  @alias('dates.today')
  today;

  @alias('dates.currentTimetableDay')
  currentTimetableDay;

  init() {
    super.init(...arguments);
    // Ensure that the dates service is initialised before we need it.
    this.dates.get('today');
  }

  // Disable ENROL arrival/leaver uploads unless specifically enabled
  // for the school, to facilitate approval testing without full release.
  @optionAlias('hero:nzMOE:enrolArrivalsLeaversEnabled')
  nzMoeEnrolArrivalsLeaversEnabled;

  @optionAlias('nzMOE:schoolType:type')
  schoolType;

  @computed('schoolType', 'jurisdiction')
  get extendedYearLevels() {
    if (
      this.jurisdiction === Jurisdiction.NZ_MOE &&
      ['23', '30', '32', '40', '41'].includes(this.schoolType)
    ) {
      return ['12', '13', '14', '15', '16'];
    }
    return [];
  }

  @computed('fieldOptions.yearLevelOptions.[]')
  get yearLevelMap() {
    if (!isEmpty(this.fieldOptions.yearLevelOptions)) {
      return this.fieldOptions.yearLevelOptions[0].options;
    }
  }

  // highestYearLevel returns the school's highest year level.
  @computed('jurisdiction', 'session.school.moeYearLevels.[]', 'yearLevelMap')
  get highestYearLevel() {
    let yearLevels;
    if (this.jurisdiction === Jurisdiction.NZ_MOE) {
      yearLevels = this.get('session.school.moeYearLevels');
    } else {
      if (!isEmpty(this.yearLevelMap)) {
        yearLevels = mapBy(
          this.yearLevelMap.slice().sort((a, b) => a.valueInt - b.valueInt),
          'label',
        );
      } else {
        yearLevels = ['8'];
      }
    }
    return yearLevels.at(-1);
  }

  @tracked yearLevel = null;
  @tracked defaultDate = '';
  @tracked defaultReason = 'S';

  get showDefaultFlags() {
    switch (this.defaultReason) {
      case 'S':
      case 'H':
      case 'K':
      case 'C':
      case 'P':
        return true;
    }
    return false;
  }

  // @tracked, not a plain field: toggleDefaultFlag rebuilds and reassigns
  // this (rather than mutating an EmberArray in place), and the template
  // reads it directly via {{includes flag.value this.defaultFlags}}.
  @tracked
  defaultFlags = null;

  allFlags = Object.freeze([
    { value: 'academic', label: 'Academic' },
    { value: 'attendance', label: 'Attendance' },
    { value: 'behavioural', label: 'Behavioural' },
    { value: 'custodial', label: 'Custodial' },
    { value: 'health', label: 'Health' },
    { value: 'personal', label: 'Personal' },
  ]);

  get isValid() {
    if (this.jurisdiction === Jurisdiction.NZ_MOE) {
      return (
        isPresent(this.yearLevel) &&
        isPresent(this.defaultDate) &&
        isPresent(this.defaultReason)
      );
    } else {
      return isPresent(this.yearLevel) && isPresent(this.defaultDate);
    }
  }

  @tracked
  loading = false;
  @tracked
  loaded = false;
  // Plain array, not TrackedArray: every mutation (getLearners, sort,
  // removeLearner, retainLastAttendanceDate, removeLastAttendanceDate)
  // rebuilds and reassigns rather than mutating in place, so a plain array
  // + @tracked reassignment is enough here. Each row's own `flags` is
  // different - see below.
  @tracked
  rows = [];
  @tracked
  processingDialogOpen = false;
  @tracked
  processing = false;
  @tracked numProcessed = 0;

  @computed('numProcessed', 'rows.length')
  get fractionProcessed() {
    if (this.numProcessed) {
      return (this.numProcessed / this.rows.length) * 100;
    }
    return 0;
  }

  @computed('rows.@each.unenrolled')
  get anyLearnerUnenrolled() {
    if (!isArray(this.rows)) {
      return false;
    }
    return this.rows.some((row) => !!row.unenrolled);
  }

  @computed('session.school.jurisdiction')
  get jurisdictionalIdLabel() {
    switch (this.session.school.jurisdiction) {
      case Jurisdiction.NZ_MOE:
        return 'NSN';
      default:
        return 'Jurisdictional ID';
    }
  }

  @tracked
  sortProp = 'date';
  @tracked
  sortDir = 'asc';
  @tracked
  detailRow = null;
  @tracked
  detailDialogOpen = false;

  @computed('detailRow.reason')
  get detailNeedsNextSchool() {
    if (isEmpty(this.detailRow)) {
      return;
    }
    switch (this.detailRow.reason) {
      case 'S':
      case 'K':
      case 'C':
      case 'P':
        return true;
    }
    return false;
  }

  @computed('detailRow.reason')
  get detailNeedsPostSchoolActivity() {
    if (isEmpty(this.detailRow)) {
      return;
    }
    switch (this.detailRow.reason) {
      case 'O':
      case 'X':
      case 'L':
      case 'E':
      case 'J':
        return true;
    }
    return false;
  }

  @computed('detailRow.reason')
  get detailNeedsFlags() {
    if (isEmpty(this.detailRow)) {
      return;
    }
    switch (this.detailRow.reason) {
      case 'S':
      case 'H':
      case 'K':
      case 'C':
      case 'P':
        return true;
    }
    return false;
  }

  reset() {
    let props = {
      yearLevel: null,
      defaultDate: '',
      defaultReason: this.jurisdiction === Jurisdiction.NZ_MOE ? 'S' : '',
      defaultFlags: [],
      loading: false,
      loaded: false,
      sortProp: 'date',
      sortDir: 'asc',
      detailRow: null,
      detailDialogOpen: false,
      rows: [],
      processingDialogOpen: false,
      processing: false,
      numProcessed: 0,
    };
    if (this.jurisdiction === Jurisdiction.NZ_MOE) {
      if (!isEmpty(this.extendedYearLevels)) {
        props.yearLevel = this.extendedYearLevels[0];
      } else if (isPresent(this.highestYearLevel)) {
        switch (true) {
          case this.endOfYear || this.isHeroAdmin:
            props.yearLevel = this.highestYearLevel;
            break;
          case this.startOfYear:
            props.yearLevel = parseInt(this.highestYearLevel, 10) + 1;
            break;
          default:
            props.yearLevel = 'pending';
            break;
        }
      }
    }
    this.setProperties(props);
  }

  @computed(
    'session.school.jurisdiction',
    'person.jurisdiction',
    'features.multiJurisdictionalEnabled',
  )
  get jurisdiction() {
    return this.get('features.multiJurisdictionalEnabled')
      ? this.get('person.jurisdiction')
      : this.get('session.school.jurisdiction');
  }

  @tracked currentMonth = moment().month();
  @optionAlias(Options.HERO_ROLLOVER_MONTH)
  rolloverMonth;

  @computed('currentMonth', 'rolloverMonth')
  get startOfYear() {
    // Align values with 0-11, so we can do some modulo maths
    let rolloverMonth = this.rolloverMonth - 1;
    return [rolloverMonth, (rolloverMonth + 1) % 12].includes(
      this.currentMonth,
    );
  }

  @computed('currentMonth', 'rolloverMonth')
  get endOfYear() {
    // Align values with 0-11, so we can do some modulo maths
    let rolloverMonth = this.rolloverMonth - 1;
    return [(rolloverMonth + 10) % 12, (rolloverMonth + 11) % 12].includes(
      this.currentMonth,
    );
  }

  @computed('session.scopes.[]')
  get isHeroAdmin() {
    return this.permissions.meet(Scopes.LINCED_ADMIN);
  }

  @computed('endOfYear', 'isHeroAdmin')
  get disableThisYear() {
    return !this.endOfYear && !this.isHeroAdmin;
  }

  @computed('startOfYear', 'isHeroAdmin')
  get disableLastYear() {
    return !this.startOfYear && !this.isHeroAdmin;
  }

  @tracked
  learnerToRetain = null;
  @tracked
  removeLastAttendanceDateDialogOpen = false;

  @action
  setDefaultReason(reason) {
    this.defaultReason = reason.value;
  }

  @action
  toggleDefaultFlag(value) {
    if (this.defaultFlags.includes(value)) {
      this.defaultFlags = this.defaultFlags.filter((item) => item !== value);
    } else {
      this.defaultFlags = [...this.defaultFlags, value];
    }
  }

  @action
  getLearners() {
    this.loading = true;

    // TODO: Handle "pending" option (find all current enrolments with an end date specified)
    let query = {
      type: PersonType.LEARNER,
      jurisdiction: this.jurisdiction,
      status: EnrolmentStatus.CURRENT,
      include: 'enrolments',
    };
    let currentLevel = this.yearLevel;
    if (!isEmpty(this.yearLevelMap)) {
      let yearValue = findBy(this.yearLevelMap, 'label', this.yearLevel);
      if (!isEmpty(yearValue)) {
        currentLevel = yearValue.valueInt;
      }
    }
    if (this.yearLevel === 'pending') {
      query.pendingUnenrolment = true;
    } else {
      query.currentLevel = currentLevel;
    }
    this.store.query('person', query).then((records) => {
      let rows = [];
      records.forEach((record) => {
        let enrolment = null;
        let date = this.defaultDate;
        let reason = this.defaultReason;
        let destinationSchool;
        let destinationArea;
        let postSchoolActivity;
        let flags = [...this.defaultFlags]; // Clone default flags.
        if (!isEmpty(record.learner.enrolmentId)) {
          enrolment = this.store.peekRecord(
            'people/enrolment',
            record.learner.enrolmentId,
          );
          if (!isEmpty(enrolment.exitDate)) {
            date = enrolment.exitDate;
          }
          if (!isEmpty(enrolment.departureReason)) {
            reason = enrolment.departureReason;
          }
          if (this.jurisdiction === Jurisdiction.NZ_MOE) {
            let metaDestinationSchool = enrolment.get(
              'meta.fields.nzMOE:destinationSchool.value',
            );
            if (!isEmpty(metaDestinationSchool)) {
              destinationSchool = metaDestinationSchool;
            }
            let metaDestinationArea = enrolment.get(
              'meta.fields.nzMOE:destinationArea.value',
            );
            if (!isEmpty(metaDestinationArea)) {
              destinationArea = metaDestinationArea;
            }
            let metaActivity = enrolment.get(
              'meta.fields.nzMOE:postSchoolActivity.value',
            );
            if (!isEmpty(metaActivity)) {
              postSchoolActivity = metaActivity;
            }
            let metaFlags = enrolment.get(
              'meta.fields.nzMOE:learnerFlags.value',
            );
            if (!isEmpty(metaFlags) && isArray(metaFlags)) {
              // Combine any existing flags with the default flags, deduped.
              flags.push(...metaFlags);
              flags = uniq(flags);
            }
          } else {
            let metaDestinationSchool = enrolment.get(
              'meta.fields.international:destinationSchool.value',
            );
            if (!isEmpty(metaDestinationSchool)) {
              destinationSchool = metaDestinationSchool;
            }
          }
        }
        rows.push({
          learner: record,
          enrolment,
          date,
          reason,
          destinationSchool,
          destinationArea,
          postSchoolActivity,
          // TrackedArray, not a plain array: toggleLearnerFlag mutates a
          // row's flags in place (push/splice) without ever reassigning
          // `row` itself (row is a plain object, not @tracked), and the
          // template reads row.flags.length directly ({{data.flags.length}}
          // badge count) plus (includes flag.value this.detailRow.flags) -
          // both need that mutation to be independently reactive.
          flags: new TrackedArray(flags),
          invalid: false,
          processing: false,
          unenrolled: false,
        });
      });
      this.rows = rows;
      this.loaded = true;
      this.loading = false;
    });
  }

  @action
  sort({ sortProp, sortDir }) {
    let sorted = sortBy(this.rows, sortProp);
    if (sortDir === 'desc') {
      sorted = sorted.reverse();
    }
    this.rows = sorted;
    this.sortProp = sortProp;
    this.sortDir = sortDir;
  }

  @action
  setReason(row, reason) {
    if (this.jurisdiction === Jurisdiction.NZ_MOE) {
      set(row, 'reason', reason.value);
    } else {
      set(row, 'reason', reason);
    }
  }

  @action
  setLearnerField(data, key, selected) {
    let newVal = isEmpty(selected) ? null : selected.value;
    set(data, key, newVal);
  }

  @action
  toggleLearnerFlag(row, value) {
    let pos = row.flags.indexOf(value);
    if (pos > -1) {
      row.flags.splice(pos, 1);
    } else {
      row.flags.push(value);
    }
  }

  @action
  needsNextSchool(reason) {
    switch (reason) {
      case 'S':
      case 'K':
      case 'C':
      case 'P':
        return true;
    }
    return false;
  }

  @action
  needsFlags(reason) {
    switch (reason) {
      case 'S':
      case 'H':
      case 'K':
      case 'C':
      case 'P':
        return true;
    }
    return false;
  }

  @action
  showWarningFlag(reason, postSchoolActivity) {
    // Warning flag should be shown if the post-school activity field is
    // required for the selected departure reason, but isn't specified.
    if (NEEDS_FIELD['postSchoolActivity'].includes(reason)) {
      return isEmpty(postSchoolActivity);
    }
    return false;
  }

  @action
  canEditDetails(reason) {
    return 'SHKCPOXLEJ'.split('').includes(reason);
  }

  @action
  editDetails(row) {
    this.detailRow = row;
    this.detailDialogOpen = true;
  }

  @action
  closeDetails() {
    this.detailRow = null;
    this.detailDialogOpen = false;
  }

  @action
  removeLearner(learner) {
    if (learner.enrolment.exitDate) {
      this.learnerToRetain = learner;
      this.removeLastAttendanceDateDialogOpen = true;
    } else {
      this.rows = this.rows.filter((row) => row !== learner);
    }
  }

  @action
  retainLastAttendanceDate() {
    this.rows = this.rows.filter((row) => row !== this.learnerToRetain);
    this.learnerToRetain = null;
    this.removeLastAttendanceDateDialogOpen = false;
  }

  @action
  removeLastAttendanceDate() {
    this.learnerToRetain.enrolment.set('exitDate', '');
    this.learnerToRetain.enrolment.save().then(() => {
      this.rows = this.rows.filter((row) => row !== this.learnerToRetain);
      this.learnerToRetain = null;
      this.removeLastAttendanceDateDialogOpen = false;
    });
  }

  @action
  saveAll() {
    this.processing = true;
    this.processingDialogOpen = true;
    this.numProcessed = 0;
    // Flag all rows as processing.
    this.rows.forEach((row) => {
      if (!row.invalid) {
        set(row, 'processing', true);
      }
    });

    let today = moment();
    let attendancePromise = Promise.resolve();
    let numOfPeriods = this.currentTimetableDay.periodIds.length;
    // Quick hack to allow schools who aren't yet taking attendance in Hero
    // to bulk unenrol their leavers at the end of the year by closing the
    // school on the last day.  Even a half day will currently require a full
    // day's attendance, so we may require further adjustment.
    let schoolOpenToday = this.today.status !== SchoolStatus.CLOSED;
    // Cater for the last day of term being morning-only.
    if (this.today.status === SchoolStatus.MORNING) {
      // Iterate periodIds as periods may not be initialised yet.
      numOfPeriods = this.currentTimetableDay.periodIds.filter((periodId) => {
        // For now, consider that any period which starts before noon must
        // have an attendance mark for a morning-only half-day.
        // It's not a guarantee that the _correct_ periods' attendance have
        // been marked, but it should work as a stop-gap measure.
        let period = this.store.peekRecord('timetable/period', periodId);
        return period.startTime <= '12:00';
      }).length;
    }
    if (schoolOpenToday && numOfPeriods > 0) {
      // Check whether any of the students are to be unenrolled as of today.
      let unenrolledToday = this.rows.filter((row) => {
        return moment(row.date).isSame(today, 'day');
      });
      if (unenrolledToday.length > 0) {
        // Get the whole school's attendance to reduce the number of queries.
        attendancePromise = this.store
          .query('attendance/record', {
            schoolId: this.get('session.activeSchool'),
            dateId: this.today.id,
          })
          .then((records) => {
            // Map records by learner ID to avoid repeated iteration.
            let recordsByLearner = records.reduce((map, record) => {
              if (isEmpty(map[record.person_id])) {
                map[record.person_id] = [];
              }
              map[record.person_id].push(record);
              return map;
            }, {});
            unenrolledToday.forEach((row) => {
              let learnerRecords = recordsByLearner[row.learner.id];
              let hasIncompleteAttendance =
                isEmpty(learnerRecords) || learnerRecords.length < numOfPeriods;
              if (hasIncompleteAttendance) {
                set(row, 'invalid', true);
                set(row, 'processing', false);
                set(
                  row,
                  'failedReason',
                  'Student has not been unenrolled as attendance has not been completed',
                );
                this.incrementProperty('numProcessed');
              }
            });
          });
      }
    }

    this.rows
      .reduce((chain, row) => {
        return chain.then(() => {
          if (!row.invalid) {
            set(row, 'processing', true);
            let props = {
              exitDate: row.date,
              departureReason: row.reason,
            };
            if (moment(row.date).isSameOrBefore(today, 'day')) {
              // Unenrol this Learner (leaving date is today or in the past).
              props.status = EnrolmentStatus.FORMER;
              set(row, 'unenrolled', true);
            }
            row.enrolment.setProperties(props);

            // Process meta fields.
            let meta = row.enrolment.get('meta');
            if (!isPresent(meta)) {
              row.enrolment.set(
                'meta',
                this.store.createFragment('people/enrolment-meta'),
              );
              meta = row.enrolment.get('meta');
            }
            if (this.jurisdiction === Jurisdiction.NZ_MOE) {
              [
                'destinationSchool',
                'destinationArea',
                'postSchoolActivity',
              ].forEach((key) => {
                // Create field if it doesn't exist yet.
                let field = meta.get(`fields.nzMOE:${key}`);
                if (isEmpty(field)) {
                  meta.addField(`nzMOE:${key}`, DataType.STRING);
                  field = meta.get(`fields.nzMOE:${key}`);
                }
                // Set the field value based on the data entered (if applicable).
                if (NEEDS_FIELD[key].includes(row.reason)) {
                  let newVal = get(row, key);
                  field.set('value', newVal);
                } else {
                  field.set('value', '');
                }
              });

              // Process flags.
              let flagsField = meta.get('fields.nzMOE:learnerFlags');
              if (isEmpty(flagsField)) {
                meta.addField('nzMOE:learnerFlags', DataType.LIST);
                flagsField = meta.get('fields.nzMOE:learnerFlags');
              }
              if (NEEDS_FIELD['flags'].includes(row.reason)) {
                flagsField.set('value', [...row.flags]);
              } else {
                flagsField.set('value', []);
              }
            } else {
              // Create field if it doesn't exist yet.
              if (!isEmpty(row.destinationSchool)) {
                let destinationSchool = meta.get(
                  `fields.international:destinationSchool`,
                );
                if (isEmpty(destinationSchool)) {
                  meta.addField(
                    `international:destinationSchool`,
                    DataType.STRING,
                  );
                  destinationSchool = meta.get(
                    `fields.international:destinationSchool`,
                  );
                }
                destinationSchool.set('value', row.destinationSchool);
              }
            }

            return row.enrolment.save().then(() => {
              set(row, 'processing', false);
              this.incrementProperty('numProcessed');
            });
          }
          return Promise.resolve();
        });
      }, attendancePromise)
      .finally(() => {
        this.processing = false;
      });
  }

  @action
  back() {
    this.loaded = false;
    this.rows = null;
    this.sortProp = 'date';
    this.sortDir = 'asc';
  }

  @action
  buttonClasses(isInvalid) {
    let classes = ['save', 'md-fab-bottom-right'];
    if (isInvalid) {
      classes.push('invalid');
    }
    return classes.join(' ');
  }

  @action
  closeProcessingDialog() {
    this.processingDialogOpen = false;

    // Force the browser to reload so that any unenrolled learners are removed from the local datastore.
    window.location.href = this.router.currentURL;
  }

  // Set destination school(text field) for international schools.
  @action
  setDestinationSchool(row, destinationSchool) {
    set(row, 'destinationSchool', destinationSchool);
  }
}
